import type React from "react"
import { useState, useEffect } from "react"
import FieldProps, { InputTypes } from "../textFieldProps"
import DropdownSelect from "react-dropdown-select"
import { getResultColorClass } from "../utilites/get-color-scoring"

interface Option {
  label: string | React.ReactElement
  value: string | number
}

const SelectField = ({
  model,
  setModel,
  campoId,
  placeholder,
  type,
  required,
  campoIdRespuesta,
  editable,
  title,
  options,
  tooltip
}: FieldProps & { editable: boolean; options: Option[] }) => {
  const [resumeValue, setResumeValue] = useState<any>(undefined)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  useEffect(() => {
    if (campoIdRespuesta && model.resume && model.resume[campoIdRespuesta] !== undefined) {
      setResumeValue(model.resume[campoIdRespuesta])
    }
  }, [model, campoIdRespuesta])

  const handleChange = (selected: Option[] | null) => {
    if (!editable || !selected || selected.length === 0) return
  
    const firstOption = selected[0]
    const esNumero = type === InputTypes.Number
    const valor = esNumero ? Number.parseFloat(String(firstOption.value)) : firstOption.value
  
    setModel((prevModel: any) => {
      if (!prevModel) return { [campoId]: valor }; 
      const newModel = Object.assign(Object.create(Object.getPrototypeOf(prevModel)), prevModel);
      newModel[campoId] = valor;
      return newModel;
    })
  }

  const selectedOption = options.find((option) => option.value === model[campoId]) || null
  const isEmpty = selectedOption === null;

  const customContentRenderer = ({ state }: { state: any }) => {
    const selectedValue = state.values && state.values.length > 0 ? state.values[0] : null

    if (!selectedValue) {
      return null;
    }

    return (
      <span 
        className="text-[#000] text-[12px]"
        title={typeof selectedValue.label === 'string' ? selectedValue.label : ''}
      >
        {selectedValue.label}
      </span>
    )
  }

  return (
    <div className="w-full text-ellipsis">
      <div className="flex items-center">
        {title && (
          <div className="flex mb-3">
            <span className="text-md text-[#4A5568] font-medium">{title}</span>
            {tooltip && (
              <div className="relative group inline-block ml-1 mt-1">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="cursor-pointer" >
                  <path d="M12 16V12M12 8H12.01M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <div className="absolute z-50 hidden group-hover:block top-full mt-2 -left-24 text-center -translate-x-1/2 px-3 py-2 bg-[#171923] text-white text-sm rounded-lg shadow-lg max-w-[412px] w-max break-words" >
                  {tooltip}
                </div>
              </div>
            )}
            <div className="flex ml-2.5 items-center">
              {resumeValue !== undefined && (
                <div className={`${typeof resumeValue === "number" ? getResultColorClass(resumeValue) : "bg-[#EDF2F7] text-[#4A5568]"} w-auto px-1 h-[16px] text-center rounded-[2px] text-xs font-normal`}>
                    {typeof resumeValue === "number" ? resumeValue.toFixed(1) : resumeValue}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      <div className={`relative w-full ${isDropdownOpen ? 'z-20' : 'z-0'}`}>
        <label
          className={`absolute left-3 text-[14px] transition-all duration-300 pointer-events-none z-10
            ${!isEmpty ? "top-[-10px] text-[12px] text-[#A0AEC0] bg-white px-1" : "top-[10px] text-[12px] text-[#A0AEC0]"}`}
        >
          {placeholder}
        </label>
        <DropdownSelect
          options={options}
          onChange={(values) => handleChange(values)}
          values={selectedOption ? [selectedOption] : []}
          placeholder={undefined}
          searchable={true}
          disabled={!editable}
          className="peer w-full"
          style={{
            borderRadius: "10px",
            height: "40px",
            borderColor: "#CBD5E0",
            borderWidth: "1px",
            fontSize: "12px",
            padding: "11px",
            paddingTop: "11px",
            backgroundColor: editable ? "white" : "#F7FAFC",
            color: "#000",
            cursor: editable ? "pointer" : "not-allowed",
          }}
          itemRenderer={({ item, methods }) => (
            <div 
              onClick={() => methods.addItem(item)}
              className="px-3 py-1.5 text-[13px] hover:bg-gray-100 cursor-pointer"
            >
              {item.label}
            </div>
          )}
          contentRenderer={customContentRenderer}
          onDropdownOpen={() => setIsDropdownOpen(true)}
          onDropdownClose={() => setIsDropdownOpen(false)}
        />
      </div>
    </div>
  )
}

export default SelectField
